/**
 * Genbrugelige UI-byggeklodser.
 *
 * Alle væsentlige handlinger har loading-, success- og fejltilstand (§4).
 * Komponenterne her leverer de tre tilstande ét sted, så de ikke skal
 * skrives forfra på hver side.
 */

import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { ApiError } from '../lib/api.js';
import { Icon, IconBadge, type IconName } from './icons.js';
import { isDepartment } from '../../shared/domain.js';

/* ------------------------------------------------------------------ */
/* Beskeder                                                            */
/* ------------------------------------------------------------------ */

/**
 * Besked til brugeren.
 *
 * Hver slags har BÅDE sin egen farve, sit eget pictogram og et skjult ord
 * («Fejl», «Advarsel» …). Ingen af de tre står alene om at forklare, hvad
 * der er sket — det er kravet i WCAG 1.4.1.
 */
const NOTICE_ICON: Record<'info' | 'warning' | 'error' | 'success', IconName> = {
  info: 'info',
  warning: 'warning',
  error: 'error',
  success: 'success',
};
const NOTICE_WORD: Record<'info' | 'warning' | 'error' | 'success', string> = {
  info: 'Information',
  warning: 'Bemærk',
  error: 'Fejl',
  success: 'Færdig',
};

export function Notice({
  kind = 'info',
  children,
}: {
  kind?: 'info' | 'warning' | 'error' | 'success';
  children: ReactNode;
}): JSX.Element {
  return (
    <div className={`notice notice--${kind}`} role={kind === 'error' ? 'alert' : 'status'}>
      <Icon name={NOTICE_ICON[kind]} size={18} />
      <div>
        <span className="sr-only">{NOTICE_WORD[kind]}: </span>
        {children}
      </div>
    </div>
  );
}

export function EmptyState({
  title,
  children,
  action,
  icon = 'info',
}: {
  title: string;
  children?: ReactNode;
  action?: ReactNode;
  /** Pictogrammet skal passe til det, der mangler — ikke være pynt. */
  icon?: IconName;
}): JSX.Element {
  return (
    <div className="empty">
      <IconBadge name={icon} tone="neutral" />
      <h3>{title}</h3>
      {children ? <p className="small">{children}</p> : null}
      {action ? <div className="mt-3">{action}</div> : null}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Sidehoved                                                           */
/* ------------------------------------------------------------------ */

/**
 * Sidens titel. Erstatter det mørke panel, hver side før startede med:
 * en overskrift behøver ikke en kasse for at være en overskrift, og
 * panelet tog en fjerdedel af en telefonskærm ved hvert sideskift.
 */
export function PageHead({
  title,
  eyebrow,
  eyebrowIcon,
  sub,
  action,
}: {
  title: string;
  eyebrow?: string;
  eyebrowIcon?: IconName;
  sub?: ReactNode;
  action?: ReactNode;
}): JSX.Element {
  return (
    <header className="row row--between">
      <div className="page-head">
        {eyebrow ? (
          <span className="page-head__eyebrow">
            {eyebrowIcon ? <Icon name={eyebrowIcon} size={15} /> : null}
            {eyebrow}
          </span>
        ) : null}
        <h1>{title}</h1>
        {sub ? <p className="page-head__sub">{sub}</p> : null}
      </div>
      {action}
    </header>
  );
}

/** Overskrift over en sektion, med pictogram og eventuelt et link ud. */
export function SectionHead({
  title,
  icon,
  id,
  action,
}: {
  title: string;
  icon: IconName;
  id?: string;
  action?: ReactNode;
}): JSX.Element {
  return (
    <div className="section__head">
      <h2 className="section__title" id={id}>
        <Icon name={icon} size={19} />
        {title}
      </h2>
      {action}
    </div>
  );
}

/**
 * En linje med pictogram og tekst: tid, sted, instruktør, familie.
 * Pictogrammet er skjult for skærmlæsere, fordi `label` skrives ud som
 * tekst lige ved siden af.
 */
export function Meta({
  icon,
  label,
  children,
}: {
  icon: IconName;
  /** Hvad linjen handler om. Læses op af skærmlæsere. */
  label: string;
  children: ReactNode;
}): JSX.Element {
  return (
    <p className="meta__item">
      <Icon name={icon} size={17} />
      <span>
        <span className="sr-only">{label}: </span>
        {children}
      </span>
    </p>
  );
}

/** «muted» bruges til noget, der IKKE sker — fx en ferielukket dag. */
export type BadgeKind =
  | 'gymnastik'
  | 'svomning'
  | 'badminton'
  | 'minigolf'
  | 'aquapunkt'
  | 'aquaspinning'
  | 'skalsibevaegelse'
  | 'important'
  | 'ok'
  | 'warning'
  | 'unread'
  | 'muted';

/**
 * Mærkatets farve for en afdeling.
 *
 * En ukendt afdeling får den neutrale grå frem for en tilfældig farve.
 * Den forrige udgave — «er det svømning? ellers gymnastik» — ville have
 * farvet badminton rødt og kaldt det gymnastik.
 */
export function departmentBadgeKind(department: string): BadgeKind {
  return isDepartment(department) ? department : 'muted';
}

export function Badge({
  kind,
  icon,
  children,
}: {
  kind?: BadgeKind;
  /** Pictogram foran teksten. Kun når det gør mærkatet hurtigere at læse. */
  icon?: IconName;
  children: ReactNode;
}): JSX.Element {
  return (
    <span className={kind ? `badge badge--${kind}` : 'badge'}>
      {icon ? <Icon name={icon} size={14} /> : null}
      {children}
    </span>
  );
}

export function Skeleton({ lines = 3 }: { lines?: number }): JSX.Element {
  return (
    <div className="stack stack--tight" aria-hidden="true">
      {Array.from({ length: lines }, (_, i) => (
        <div key={i} className="skeleton" style={{ width: i === lines - 1 ? '60%' : '100%' }} />
      ))}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Datahentning                                                        */
/* ------------------------------------------------------------------ */

export interface AsyncState<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
  reload: () => void;
}

/**
 * Henter data og holder styr på indlæsning og fejl.
 * `deps` styrer, hvornår der hentes igen.
 */
export function useAsync<T>(loader: () => Promise<T>, deps: readonly unknown[] = []): AsyncState<T> {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    loader()
      .then((result) => {
        if (!cancelled) setData(result);
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setError(err instanceof ApiError ? err.message : 'Der opstod en uventet fejl.');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, nonce]);

  const reload = useCallback(() => setNonce((n) => n + 1), []);
  return { data, loading, error, reload };
}

/** Standardramme om en side, der henter data. */
export function AsyncSection<T>({
  state,
  children,
  skeletonLines = 4,
}: {
  state: AsyncState<T>;
  children: (data: T) => ReactNode;
  skeletonLines?: number;
}): JSX.Element {
  if (state.loading && !state.data) {
    return (
      <div className="card">
        <Skeleton lines={skeletonLines} />
        <span className="sr-only">Indlæser …</span>
      </div>
    );
  }
  if (state.error) {
    return (
      <div className="stack">
        <Notice kind="error">{state.error}</Notice>
        <button type="button" className="btn btn--secondary" onClick={state.reload}>
          Prøv igen
        </button>
      </div>
    );
  }
  if (!state.data) return <></>;
  return <>{children(state.data)}</>;
}

/* ------------------------------------------------------------------ */
/* Handlinger                                                          */
/* ------------------------------------------------------------------ */

export interface ActionState {
  run: (fn: () => Promise<void>) => Promise<void>;
  pending: boolean;
  error: string | null;
  success: string | null;
  setSuccess: (message: string | null) => void;
  fieldError: (field: string) => string | undefined;
  clear: () => void;
}

/**
 * Kører en handling med loading-, success- og fejltilstand.
 * Feltfejl fra serveren gøres tilgængelige for formularen.
 */
export function useAction(): ActionState {
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [fields, setFields] = useState<Array<{ field: string; message: string }>>([]);

  const run = useCallback(async (fn: () => Promise<void>) => {
    setPending(true);
    setError(null);
    setFields([]);
    try {
      await fn();
    } catch (err: unknown) {
      if (err instanceof ApiError) {
        setError(err.message);
        setFields(err.fields);
      } else {
        setError('Der opstod en uventet fejl. Prøv igen.');
      }
    } finally {
      setPending(false);
    }
  }, []);

  const clear = useCallback(() => {
    setError(null);
    setSuccess(null);
    setFields([]);
  }, []);

  const fieldError = useCallback(
    (field: string) => fields.find((f) => f.field === field)?.message,
    [fields],
  );

  return { run, pending, error, success, setSuccess, fieldError, clear };
}

export function ActionFeedback({ state }: { state: ActionState }): JSX.Element {
  return (
    <>
      {state.error ? <Notice kind="error">{state.error}</Notice> : null}
      {state.success ? <Notice kind="success">{state.success}</Notice> : null}
    </>
  );
}

/** Knap med indbygget loading-tilstand og korrekt aria. */
export function ActionButton({
  onClick,
  pending,
  children,
  className = 'btn',
  disabled,
  type = 'button',
}: {
  onClick?: () => void;
  pending?: boolean;
  children: ReactNode;
  className?: string;
  disabled?: boolean;
  type?: 'button' | 'submit';
}): JSX.Element {
  return (
    <button
      type={type}
      className={className}
      onClick={onClick}
      disabled={disabled || pending}
      aria-busy={pending ? 'true' : undefined}
    >
      {pending ? <span className="spinner" aria-hidden="true" /> : null}
      {children}
    </button>
  );
}

/* ------------------------------------------------------------------ */
/* Formularfelter                                                      */
/* ------------------------------------------------------------------ */

export function Field({
  label,
  hint,
  error,
  htmlFor,
  children,
}: {
  label: string;
  hint?: string;
  error?: string;
  htmlFor: string;
  children: ReactNode;
}): JSX.Element {
  return (
    <div className="field">
      <label htmlFor={htmlFor}>{label}</label>
      {hint ? <span className="field__hint">{hint}</span> : null}
      {children}
      {error ? (
        <span className="field__error" role="alert">
          {error}
        </span>
      ) : null}
    </div>
  );
}

/** Filterknapper med korrekt aria-pressed. */
export function ChipFilter<T extends string>({
  options,
  value,
  onChange,
  label,
}: {
  options: Array<{ value: T; label: string }>;
  value: T;
  onChange: (value: T) => void;
  label: string;
}): JSX.Element {
  return (
    <div className="filter-bar" role="group" aria-label={label}>
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          className="chip"
          aria-pressed={value === option.value}
          onClick={() => onChange(option.value)}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}
